(() => {
  const enhancedClass = 'askr-infinite-scroll--enhanced';
  const loadingClass = 'askr-infinite-scroll--loading';
  const completeClass = 'askr-infinite-scroll--complete';
  const revealedClass = 'askr-product-reveal--visible';

  const getNextUrl = (scroller) => scroller?.dataset.nextUrl || '';

  const revealItems = (items) => {
    if (!items.length) return;

    if (!('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add(revealedClass));
      return;
    }

    const observer = new IntersectionObserver(
      (entries, currentObserver) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(revealedClass);
          currentObserver.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 },
    );

    items.forEach((item, index) => {
      item.style.setProperty('--askr-reveal-order', String(Math.min(index, 8)));
      observer.observe(item);
    });
  };

  const loadNextPage = async (scroller, grid) => {
    if (!scroller || !grid || scroller.dataset.loading === 'true') return;

    const nextUrl = getNextUrl(scroller);
    if (!nextUrl) {
      scroller.classList.add(completeClass);
      return;
    }

    scroller.dataset.loading = 'true';
    scroller.classList.add(loadingClass);

    try {
      const response = await fetch(nextUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Collection page request failed: ${response.status}`);

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = parsed.querySelector('#product-grid');
      const nextScroller = parsed.querySelector('[data-askr-infinite-scroll]');
      const nextItems = nextGrid ? [...nextGrid.querySelectorAll('.grid__item')] : [];

      nextItems.forEach((item) => {
        item.classList.add('askr-product-reveal');
        item.classList.remove(revealedClass);
        grid.appendChild(item);
      });

      revealItems(nextItems);

      scroller.dataset.nextUrl = nextScroller?.dataset.nextUrl || '';
      scroller.dataset.currentPage = nextScroller?.dataset.currentPage || scroller.dataset.currentPage || '';
      scroller.dataset.totalPages = nextScroller?.dataset.totalPages || scroller.dataset.totalPages || '';

      if (!getNextUrl(scroller)) scroller.classList.add(completeClass);
    } catch (error) {
      console.error(error);
      scroller.classList.remove(enhancedClass);
    } finally {
      scroller.dataset.loading = 'false';
      scroller.classList.remove(loadingClass);
    }
  };

  const initInfiniteScroller = (container = document) => {
    const grid = container.querySelector?.('#product-grid') || document.querySelector('#product-grid');
    const scroller =
      container.querySelector?.('[data-askr-infinite-scroll]') || document.querySelector('[data-askr-infinite-scroll]');

    if (!grid || !scroller || scroller.dataset.askrInitialized === 'true') {
      revealItems([...(grid?.querySelectorAll?.('.askr-product-reveal:not(.askr-product-reveal--visible)') || [])]);
      return;
    }

    scroller.dataset.askrInitialized = 'true';
    scroller.dataset.loading = 'false';
    scroller.classList.add(enhancedClass);

    revealItems([...grid.querySelectorAll('.askr-product-reveal')]);

    if (!('IntersectionObserver' in window)) return;

    const sentinel = scroller.querySelector('.askr-infinite-scroll__sentinel') || scroller;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) loadNextPage(scroller, grid);
        });
      },
      { rootMargin: '900px 0px 600px 0px', threshold: 0 },
    );

    observer.observe(sentinel);
  };

  const initMobileRefine = () => {
    const mobileQuery = window.matchMedia('(max-width: 749px)');
    const refine = document.querySelector('[data-askr-mobile-refine]');
    if (!refine) return;

    const syncRefineState = () => {
      if (mobileQuery.matches && refine.dataset.askrRefineActive !== 'true') {
        refine.removeAttribute('open');
      } else {
        refine.setAttribute('open', '');
      }
    };

    syncRefineState();
    mobileQuery.addEventListener?.('change', syncRefineState);
  };

  const watchFacetRenders = () => {
    const container = document.getElementById('ProductGridContainer');
    if (!container || container.dataset.askrObserverReady === 'true') return;

    container.dataset.askrObserverReady = 'true';
    const observer = new MutationObserver(() => initInfiniteScroller(container));
    observer.observe(container, { childList: true, subtree: true });
  };

  document.addEventListener('DOMContentLoaded', () => {
    initMobileRefine();
    initInfiniteScroller(document);
    watchFacetRenders();
  });
})();
